import { type LevelReading, levelWords } from '@/capture/level';
import { useT } from '@/i18n/runtime';

/**
 * The level: a horizon line that stays parallel to the WORLD horizon (it rotates against the
 * phone's lean), a short fixed reference tick, and the words beside it — "Level" or "3° left".
 * Words carry the state; the solid/dashed line is reinforcement, never the only signal (WCAG 1.4.1).
 *
 * Rotation: the screen is physically leaning by −roll (level.ts: roll < 0 = top edge to the right,
 * i.e. clockwise). A line drawn at CSS rotate(roll) — CSS positive is clockwise on screen — therefore
 * cancels the lean and reads horizontal in the world. Reduced motion disables the transition (global rule).
 *
 * Not a live region: a 10 Hz reading must not be announced 10 times a second. The text is plain
 * content a screen-reader user can read on demand.
 */
export function LevelIndicator({ reading }: { reading: LevelReading }): JSX.Element {
  const { t } = useT();
  const words = levelWords(reading);
  const text = words.key === 'sv.capture.level.level' ? t(words.key) : t(words.key, { deg: String(words.deg) });
  return (
    <div className={`sv-level-wrap${reading.level ? ' is-level' : ''}`} data-testid="level-indicator" data-level={reading.level ? 'true' : 'false'}>
      <span className="sv-level-ref" aria-hidden="true" />
      <span className="sv-level-line" aria-hidden="true" style={{ transform: `translate(-50%, -50%) rotate(${reading.roll.toFixed(1)}deg)` }} />
      <span className="sv-level-text mono">{text}</span>
    </div>
  );
}
